<!-- CONTENT -->
<div
    #scrollDiv
    animate.enter="animate-fade-in-panel"
    class="flex grow flex-col overflow-x-hidden overflow-y-auto p-12 motion-reduce:animate-none"
    interactaScrollTracker
    [scrollOffsetPx]="100"
    [scrollToTop]="true"
    [wheelScrollOnly]="true"
    (scrolled)="onScroll($event)"
>
    @if (messagesFetching() && !hasMessages()) {
        <div class="flex size-full items-center justify-center">
            <interacta-loader [size]="'large'" />
        </div>
    } @else if (!hasMessages()) {
        @if (activeAgent(); as agent) {
            <div class="flex flex-col items-center gap-y-16">
                <div
                    class="size-160 overflow-hidden"
                    [class.rounded-full]="!agent.avatarFull"
                >
                    @if (agent.avatarFull || agent.avatar) {
                        <img
                            class="size-full object-cover"
                            [alt]="agent.name"
                            [src]="agent.avatarFull ?? agent.avatar"
                        />
                    } @else {
                        <div
                            class="flex size-full items-center justify-center"
                            [class]="activeAgentColorClass()"
                        >
                            <span class="typo-t1">
                                {{ activeAgentAbbreviation() }}
                            </span>
                        </div>
                    }
                </div>
                <p class="typo-sm">
                    {{ agent.welcomeMessage ?? agent.description }}
                </p>
            </div>
        } @else if (isBlockedByTokenLimit()) {
            <div class="size-full">
                <h6 class="mb-16 text-center typo-t6">
                    {{ 'AI.SIDE_PANEL.CREDIT_LIMIT_TITLE' | translate }}
                </h6>
                <interacta-image
                    alt=""
                    class="m-auto block size-160"
                    [src]="'assets/shared/images/doodles/performance.webp'"
                />
                <p
                    class="mt-16 text-center typo-sm"
                    [innerHtml]="
                        remainingDays
                            | i18nPlural
                                : {
                                      '=1': 'AI.SIDE_PANEL.CREDIT_LIMIT_DESCRIPTION_SING',
                                      other: 'AI.SIDE_PANEL.CREDIT_LIMIT_DESCRIPTION_PLUR',
                                  }
                            | translate: { count: remainingDays }
                    "
                ></p>
            </div>
        } @else {
            <div class="size-full">
                <h6
                    class="mb-16 text-center typo-t6"
                    [innerHtml]="
                        'AI.SIDE_PANEL.USER_GREETINGS'
                            | translate: { firstName: currentUserFirstName() }
                    "
                ></h6>
                <interacta-image
                    alt=""
                    class="m-auto block size-160"
                    [src]="'assets/shared/images/doodles/knowledge-management.png'"
                />
                <p
                    class="mt-16 text-center typo-sm"
                    [innerHtml]="'AI.SIDE_PANEL.DESCRIPTION' | translate"
                ></p>
            </div>
        }
    } @else {
        <div #loadMoreContainer>
            @if (
                messagesNextPageToken().tag === 'regularLoading' ||
                messagesFetching()
            ) {
                <interacta-load-more
                    class="mb-16"
                    [arrow]="'up'"
                    [isLoading]="messagesFetching()"
                    [label]="'SHARED.SHOW_MORE' | translate"
                    [size]="'small'"
                    (loadMore)="loadOlderMessages()"
                />
            }
        </div>

        <ng-template
            #messageTemplate
            let-entry="entry"
            let-index="index"
            let-last="last"
        >
            <div
                class="ai-convo-message mb-16 flex flex-col"
                tabindex="-1"
                [class.ai-convo-message-user]="entry.role === AISharedRole.USER"
            >
                @if (entry.role === AISharedRole.USER) {
                    <interacta-ai-shared-user-message-balloon
                        class="w-full"
                        [message]="entry"
                    />
                }
                @if (entry.role === AISharedRole.MODEL) {
                    @if (entry.agent; as agent) {
                        <span
                            aria-hidden="true"
                            class="mb-4 inline-block w-fit px-4 typo-xs-bold text-text-on-avatar"
                            [class]="agent.color | apply: getAgentColorClass"
                        >
                            {{ '@' + agent.name }}
                        </span>
                    }
                    <interacta-ai-shared-model-message-balloon
                        [enableRevealAnimation]="true"
                        [isLast]="last"
                        [message]="entry"
                        [senderName]="entry.agent?.name ?? null"
                        [smartActionsDisabled]="isChatFetching()"
                        (askReference)="askReference($event)"
                        (messageAnimated)="onMessageAnimated(entry.id)"
                        (retry)="retryMessage(index)"
                        (revealed)="onMessageRevealed(entry.id)"
                        (sendFeedbackDetail)="sendFeedbackDetail(entry, $event)"
                        (sendFeedbackLike)="sendFeedbackLike(entry, $event)"
                        (smartAction)="sendSmartAction($event)"
                    >
                        <interacta-ai-convo-thinking-steps
                            aiSharedBeforeMessage
                            [steps]="entry.steps"
                        />
                    </interacta-ai-shared-model-message-balloon>
                }
            </div>
        </ng-template>

        @for (row of messageRows().history; track row.entry.id) {
            <ng-container
                [ngTemplateOutlet]="messageTemplate"
                [ngTemplateOutletContext]="row"
            />
        }
        <div
            aria-live="polite"
            aria-relevant="additions"
            class="flex flex-col"
            role="log"
        >
            @for (row of messageRows().live; track row.entry.id) {
                <ng-container
                    [ngTemplateOutlet]="messageTemplate"
                    [ngTemplateOutletContext]="row"
                />
            }
        </div>
        @for (row of messageRows().revealing; track row.entry.id) {
            <ng-container
                [ngTemplateOutlet]="messageTemplate"
                [ngTemplateOutletContext]="row"
            />
        }

        @if (isChatFetching() && activeAgent()) {
            <div class="flex w-full gap-x-8">
                <interacta-ai-loader
                    class="shrink-0"
                    [isDark]="currentThemeMode() === 'dark'"
                />
                <p class="flex grow flex-col gap-y-8">
                    @for (_ of 7 | array; track $index) {
                        <span
                            class="h-8 animate-pulse rounded-3xl bg-surface-300"
                            [ngClass]="{
                                'w-full': !$last,
                                'w-4/5': $last,
                            }"
                        ></span>
                    }
                </p>
            </div>
        }
    }

    <div class="empty:contents" role="status">
        @if (hasMessages() && isChatFetching() && !activeAgent()) {
            <interacta-ai-convo-thinking-line
                [isDark]="currentThemeMode() === 'dark'"
                [steps]="thinkingSteps()"
            />
        }
    </div>

    <interacta-live-status
        [message]="
            hasMessages() && isChatFetching() && activeAgent()
                ? ('AI.THINKING.GENERIC.PREPARING_ANSWER' | translate)
                : null
        "
    />

    @if (hasMessages()) {
        <!-- Reserves only the scroll room still missing to fill one viewport, so
        the last user message can be anchored to the top with the AI answer below.
        Height is computed imperatively in anchorLastUserMessageTop(). -->
        <div #bottomSpacer aria-hidden="true" class="shrink-0"></div>
    }
</div>
<!-- /CONTENT -->

<!-- TEXT INPUT -->
@if (!isBlockedByTokenLimit()) {
    <div
        animate.enter="animate-fade-in-panel"
        class="flex shrink-0 flex-col overflow-hidden p-8 motion-reduce:animate-none"
    >
        @if (pendingAttachment(); as attachment) {
            <div
                class="mb-8 grid grid-cols-[minmax(0,max-content)_auto] items-center justify-start gap-x-8"
            >
                <interacta-ai-shared-attachment-chip
                    class="block min-w-0"
                    [attachment]="attachment"
                    [size]="'small'"
                    (attachmentClick)="openPendingAttachment(attachment)"
                />
                <button
                    interacta-icon-button
                    type="button"
                    [icon]="'close'"
                    [label]="'AI.REMOVE_ATTACHMENT' | translate"
                    [size]="'small'"
                    (click)="clearPendingAttachment()"
                ></button>
            </div>
        }
        @if (pendingReference(); as reference) {
            <div
                class="mb-8 grid grid-cols-[minmax(0,max-content)_auto] items-center justify-start gap-x-8"
            >
                <interacta-ai-shared-reference-chip
                    class="block min-w-0"
                    [reference]="reference"
                    [size]="'small'"
                    (referenceClick)="openPendingReference(reference)"
                />
                <button
                    interacta-icon-button
                    type="button"
                    [icon]="'close'"
                    [label]="'AI.REMOVE_SOURCE' | translate"
                    [size]="'small'"
                    (click)="clearPendingReference()"
                ></button>
            </div>
        }
        @if (postSuggestions().length) {
            <div class="mb-8 flex flex-wrap gap-8">
                @for (suggestion of postSuggestions(); track suggestion.id) {
                    <button
                        class="relative flex w-max max-w-full items-center gap-x-4 overflow-hidden rounded-3xl border border-border-text bg-surface-A px-12 py-8 after:absolute after:inset-0 after:content-[''] hover:after:bg-hovered-layer active:after:bg-pressed-layer"
                        type="button"
                        (click)="sendSuggestion(suggestion)"
                    >
                        <interacta-icon
                            class="size-16 shrink-0"
                            [icon]="suggestion.icon"
                        />
                        <span class="text-left typo-xs">{{
                            suggestion.label | translate
                        }}</span>
                    </button>
                }
            </div>
        }
        @if (isChipVisible()) {
            <interacta-quick-action-chip
                class="mb-8"
                [active]="communityFilter() !== null"
                [count]="chipCount()"
                [icon]="'search-left'"
                [label]="chipLabel()"
                [mode]="chipMode()"
                (chipClick)="onChipClick()"
            />
        }
        <div class="flex items-end gap-x-8">
            <div class="relative grow">
                <interacta-ai-shared-input
                    #input
                    [control]="questionControl"
                    [hasLeadingAction]="postContextId() !== null"
                    [maxLength]="questionLimit()"
                    (keydown.enter)="sendMessage(questionControl.value)"
                >
                    @if (postContextId(); as postId) {
                        <interacta-ai-shared-attachment-picker
                            [disabled]="pendingAttachment() !== null"
                            [postId]="postId"
                            [themeMode]="currentThemeMode()"
                            (attachmentSelected)="selectAttachment($event)"
                        />
                    }
                </interacta-ai-shared-input>
            </div>

            @if (questionControl.value.length > 0 && !isChatFetching()) {
                <button
                    interacta-icon-button
                    type="submit"
                    [icon]="'paper-plane'"
                    [label]="'AI.SEND_MESSAGE' | translate"
                    [size]="'large'"
                    [style]="'filled'"
                    (click)="sendMessage(questionControl.value)"
                ></button>
            }
        </div>
        <div class="empty:contents" role="status">
            @if (questionLimit(); as limit) {
                @if (questionControl.value.length >= limit) {
                    <p class="px-20 pt-4 typo-sm text-text-mid-contrast">
                        {{
                            'AI.QUESTION_LIMIT_REACHED'
                                | translate: { count: limit }
                        }}
                    </p>
                }
            }
        </div>
    </div>
}
<!-- /TEXT INPUT -->

<interacta-communities-multiple-select-dialog
    [cancelLabel]="'SHARED.CONFIRM_DIALOG.DEFAULT_CANCEL' | translate"
    [communityIdsControl]="communityIdsControl"
    [confirmLabel]="'UI.BUTTON.SAVE' | translate"
    [isOpen]="showCommunityPicker()"
    [title]="'AI.SIDE_PANEL.COMMUNITY_FILTER_DIALOG_TITLE' | translate"
    [workspaces]="communityWorkspaces()"
    (confirm)="onCommunityPickerConfirm()"
    (emitCancel)="onCloseDialog()"
/>
